import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles, categories, siteConfig } from '@/mocks/siteData';
import { JsonLd, getBreadcrumbSchema } from '@/components/base/JsonLd';

function formatReadCount(count: number): string {
  if (count >= 10000) {
    return `${(count / 10000).toFixed(1)}万`;
  }
  return count.toString();
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 pb-20 text-center">
          <div className="max-w-lg mx-auto px-4">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-primary-100 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-primary-600 text-3xl"></i>
            </div>
            <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-3">分类未找到</h1>
            <p className="text-foreground-500 mb-8">这个分类可能不存在哦~</p>
            <Link to="/" className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white font-semibold hover:bg-primary-600 transition-colors duration-200 cursor-pointer text-sm">
              <i className="ri-arrow-left-line"></i>返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const categoryArticles = articles.filter((a) => a.category === slug);

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - ${siteConfig.name}`,
    description: category.description,
    url: `http://${siteConfig.domain}/category/${category.slug}`,
    hasPart: categoryArticles.map((a) => ({
      '@type': 'Article',
      headline: a.title,
      url: `http://${siteConfig.domain}/article/${a.slug}`,
      datePublished: a.publishDate,
    })),
  };

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: '首页', url: `http://${siteConfig.domain}` },
    { name: category.name, url: `http://${siteConfig.domain}/category/${category.slug}` },
  ]);

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={collectionSchema} />
      <JsonLd data={breadcrumbSchema} />
      <Navbar />

      <main>
        <section className="relative w-full min-h-[400px] md:min-h-[480px] flex items-center">
          <div className="absolute inset-0">
            <img src="https://readdy.ai/api/search-image?query=Chinese%20traditional%20red%20silk%20fabric%20texture%20background%2C%20elegant%20abstract%20composition%2C%20warm%20crimson%20and%20gold%20tones%2C%20luxurious%20textile%20pattern%2C%20soft%20flowing%20folds%2C%20artistic%20minimalist%20design%2C%20rich%20warm%20color%20palette&width=1800&height=900&seq=category-hero-generic&orientation=landscape" alt={category.name} className="w-full h-full object-cover object-center" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/70"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 pt-20">
            <div className="max-w-4xl mx-auto text-center">
              <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-white/15 backdrop-blur-sm border border-white/20 flex items-center justify-center">
                <i className={`${category.icon} text-white text-2xl`}></i>
              </div>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-black text-white mb-4">{category.name}</h1>
              <p className="text-base md:text-lg text-white/70 max-w-xl mx-auto leading-relaxed">{category.description}</p>
              <p className="mt-4 text-sm text-white/50">共 {categoryArticles.length} 篇爆料</p>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-1.5 h-5 rounded-full bg-primary-500"></div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">全部文章</h2>
            </div>
            {categoryArticles.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-inbox-line text-foreground-400 text-2xl"></i>
                </div>
                <p className="text-foreground-500">暂无文章，敬请期待~</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {categoryArticles.map((article) => (
                  <Link key={article.id} to={`/article/${article.slug}`} className="group cursor-pointer">
                    <div className="rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300">
                      <div className="aspect-[16/10] overflow-hidden relative">
                        <img src={article.coverImage} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                        {article.isHot && <div className="absolute top-3 right-3"><span className="px-2 py-1 rounded-md bg-accent-500 text-white text-xs font-bold">HOT</span></div>}
                        {article.isNew && <div className="absolute top-3 right-3"><span className="px-2 py-1 rounded-md bg-primary-500 text-white text-xs font-bold">NEW</span></div>}
                      </div>
                      <div className="p-4">
                        <h3 className="font-heading text-sm md:text-base font-bold text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors duration-200">{article.title}</h3>
                        <p className="mt-2 text-xs text-foreground-500 line-clamp-2 leading-relaxed">{article.excerpt}</p>
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-background-200/70">
                          <div className="flex items-center gap-3 text-xs text-foreground-400">
                            <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{formatReadCount(article.readCount)}</span>
                            <span className="flex items-center gap-1"><i className="ri-chat-3-line"></i>{formatReadCount(article.commentCount)}</span>
                          </div>
                          <span className="text-xs text-foreground-300">{article.publishDate}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-100">
          <div className="px-4 md:px-6 max-w-7xl mx-auto text-center">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8">探索更多分类</h2>
            <div className="flex flex-wrap justify-center gap-3">
              {categories.filter((c) => c.slug !== slug).map((cat) => (
                <Link key={cat.slug} to={`/category/${cat.slug}`} className="whitespace-nowrap inline-flex items-center gap-2 px-5 py-3 rounded-full bg-background-50 border border-background-200 hover:border-primary-300 hover:bg-primary-50 transition-all duration-200 cursor-pointer">
                  <i className={`${cat.icon} text-primary-600`}></i>
                  <span className="text-sm font-medium text-foreground-800">{cat.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
